import { useEffect, useMemo, useState } from 'react';
import { getPaginatedItems } from 'src/utils/pagination/getPaginatedItems';

interface State {
  itemsPerPage: number;
  page: number;
}

interface Hooks<T> extends State {
  paginated: T[];
  onPageChange: (values: PageChangeProps) => void;
  total: number;
}

interface PageChangeProps {
  page: number;
  itemsPerPage: number;
}

export const usePagination = <T>(items: T[], initialPage: number = 1, initialItemsPerPage: number = 10): Hooks<T> => {
  const [{ page, itemsPerPage }, setState] = useState<State>({
    page: initialPage,
    itemsPerPage: initialItemsPerPage,
  });

  useEffect(() => {
    const minPageItems = itemsPerPage * (page - 1);
    const isLastPageItemDeleted = page > 1 && items.length === minPageItems;

    if (isLastPageItemDeleted) {
      setState((prevState) => ({ ...prevState, page: page - 1 }));
    }
  }, [items, itemsPerPage, page]);

  const onPageChange = ({ itemsPerPage, page }: PageChangeProps) => {
    setState((prevState) => ({ ...prevState, itemsPerPage, page }));
  };

  const paginated = useMemo(() => getPaginatedItems({ items, itemsPerPage, page }), [items, itemsPerPage, page]);

  return {
    itemsPerPage,
    onPageChange,
    page,
    paginated,
    total: items.length,
  };
};
